import React, { useEffect, useMemo, useState } from 'react'
import {
	useAuth,
	getSessionUser,
	fetchWechatQrUrl,
	refreshSessionAfterWechat,
	requestCode,
	verifyCode,
} from './auth'
import { BRAND_SEAL } from '../brand'
import { PageHeader } from '../PageHeader'
import { SiteFooter } from '../SiteFooter'
import { ThemeContextValue, tbClass, uiPrimitives, useTheme, useThemedStyles } from '../theme'
import { CredentialModal, credentialPromptMuted } from './Credentials'
import { LegalDocModal } from './LegalDocModal'
import { SHARE_DISCLAIMER, USER_AGREEMENT } from './registrationAgreement'

/**
 * 登录页 —— 微信扫码（唯一注册入口） + 邮箱验证码（登录与第二凭证）。
 *
 * ## 为什么没有注册与找回密码
 *
 * 2026-10-21 定稿的方案 A：**微信扫码是唯一的注册入口**，全站不设密码。
 * 同一个人若既能微信注册、又能邮箱注册，必然出现两份账号，而两份账号之间
 * 没有任何可验证的等价关系（微信不给邮箱），之后只能人工猜着合并。
 * 关掉自助注册，重复账号从源头消失。
 *
 * 邮箱页签保留的是**登录**能力：存量邮箱账号、以及绑过邮箱的微信账号
 * 都能用它进来。凭证是验证码，不是密码 —— 没有密码可忘。
 *
 * 版面照 `_v2-designs/jiangnan-qinan.html` 的「登录」样张：顶栏只留一个出口
 * （先不登录，直接用），表单卡片浮在**凹槽色**地面上而不是再套一张白卡 ——
 * 这样页面上只有一次"纸"的层次，印章与输入框才立得住。
 *
 * ## 协议勾选（2026-10-28 补回）
 *
 * 原来是邮箱注册表单里的勾选框，改成微信扫码后跟着表单一起消失了 ——
 * 同一个动作现在**既是注册也是登录**，没法在扫码前问一句"你是新用户吗"。
 * 处理办法是：把二维码挡在勾选之后。没勾选就不取二维码（`loadQr` 里
 * 直接 return），框里显示提示；勾选后才去取，并把协议版本随请求带给后端，
 * 由它在回调里真正建号那一刻写进 `auth_user`。
 *
 * 一颗勾选框覆盖**两份**文档：《用户服务协议》管账号本身，《曲谱公开分享
 * 免责声明》管以后公开作品的责任。分成两颗会让人以为可以只同意其中一份 ——
 * 而只同意账号协议的人，把谱公开到广场时仍然是没授权的。分别点开还能看全文。
 *
 * 代价是所有人在本页都要点一次勾（后端只给**新建**账号记录同意，老用户
 * 扫码登录不受影响）；换来的是"没同意过的人一定不会建出账号"，以及一条
 * 能拿得出手的同意记录。两条都别省。
 */
const stylesFactory = (t: ThemeContextValue) => ({
	// 主按钮直接复用全站那一套（tb / tb-primary），不在这里另画一个紫色按钮
	...uiPrimitives(t),

	/**
	 * 纸底（`paper3`）从 `auth` 上移到这一层 —— 页脚要接在卡片下面。
	 *
	 * 底色若留在 `auth` 上就不行了：`auth` 是 `flex:1`，页脚一进来它就矮一截，
	 * 纸底面板跟着提前结束，下面露出一段裸纸，横向多出一道断层。
	 */
	authWrap: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		minHeight: 0,
		background: t.palette.paper3,
	},
	/** 卡片仍在这一层居中；矮下去的只是它的外框，视觉重心只往上挪了小半行 */
	auth: {
		flex: 1,
		display: 'grid',
		placeItems: 'center',
		padding: '44px 22px',
		[`@media (max-width: ${t.breakpoint}px)`]: {
			padding: '28px 16px',
		},
	},
	/**
	 * 页脚不跟卡片同宽（卡片封顶 352 且居中，跟它对齐反而像卡片的一部分），
	 * 而是对齐顶栏那一条内容列 —— `PageHeader.inner` 自己没有横向内边距，
	 * 所以几何得照 `headerInner` 抄一份：`maxWidth:1120` + 左右 20px（窄屏 12px）。
	 *
	 * ⚠️ 这层得待在**普通块级**上下文里（外面那个无名 div 就是为这个），不能直接当
	 *    `authWrap` 的 flex 子项：`margin:0 auto` 是交叉轴上的 auto 外边距，它会
	 *    取消 `align-items:stretch`，于是还得手写 `width:100%` 才能撑开 —— 而
	 *    `width:100%` 算的是**内容宽**，再加上这层自己的左右内边距，窄屏就横向溢出了。
	 *    块级上下文里 `width:auto` 没有这个毛病。
	 */
	footInner: {
		maxWidth: 1120,
		margin: '0 auto',
		padding: '0 20px 20px',
		[`@media (max-width: ${t.breakpoint}px)`]: {
			padding: '0 12px 16px',
		},
	},
	card: {
		width: '100%',
		maxWidth: 352,
	},
	/** 匾额体写一个「弹」：与顶栏印章同源，是页面上唯一的朱砂块 */
	seal: {
		width: 46,
		height: 46,
		margin: '0 auto 20px',
		display: 'grid',
		placeItems: 'center',
		background: t.palette.cinnabar,
		color: '#fff',
		borderRadius: t.radius.sm,
		fontFamily: t.fonts.brush,
		fontSize: 22,
		lineHeight: 1,
		userSelect: 'none',
	},
	// h3/p 带着浏览器默认的上外边距，这里必须显式归零，否则印章与标题之间会多出一大截
	title: {
		textAlign: 'center',
		fontSize: 19,
		letterSpacing: '.04em',
		margin: '0 0 6px',
	},
	sub: {
		textAlign: 'center',
		fontSize: 12.5,
		color: t.palette.ink3,
		margin: '0 0 24px',
	},
	field: {
		marginBottom: 15,
	},
	label: {
		display: 'block',
		fontFamily: t.fonts.serif,
		fontSize: 12.5,
		color: t.palette.ink2,
		marginBottom: 5,
	},
	input: {
		width: '100%',
		font: 'inherit',
		fontFamily: t.fonts.sans,
		fontSize: 14,
		padding: '9px 11px',
		border: `1px solid ${t.palette.line2}`,
		borderRadius: t.radius.sm,
		background: t.palette.paper2,
		color: t.palette.ink,
		outline: 'none',
		boxSizing: 'border-box',
		'&:focus': {
			borderColor: t.palette.celadon,
			boxShadow: `0 0 0 3px ${t.palette.celadon2}`,
		},
	},
	/**
	 * 验证码一行：输入框 + 发送按钮。
	 *
	 * 按钮固定 104px 宽 —— 文案会在「获取验证码 / 45 秒后重发 / 重新发送」
	 * 之间变，宽度跟着变的话输入框会左右抽动，看着像页面在抖。
	 */
	codeRow: {
		display: 'flex',
		gap: 8,
	},
	codeInput: {
		flex: 1,
		minWidth: 0,
	},
	sendBtn: {
		flex: 'none',
		width: 104,
		fontSize: 12.5,
		padding: '0 6px',
		whiteSpace: 'nowrap',
	},
	/** 主按钮铺满卡片宽度：这一页只有一个主动作 */
	submit: {
		width: '100%',
		padding: '10px 0',
		fontSize: 14,
		textAlign: 'center',
	},
	alt: {
		display: 'flex',
		justifyContent: 'space-between',
		fontSize: 12.5,
		// 链接自带 8px 上下内边距撑触控高度，这里补回设计稿的 14px 视觉间距
		marginTop: 6,
		flexWrap: 'wrap',
		gap: 8,
	},
	/**
	 * login 模式下那行「没有微信？」的额外上边距。
	 *
	 * 单独一条而不是改 `alt`：页签只在 login 模式出现，register/forgot
	 * 模式下主按钮与 alt 之间不需要这段空白。
	 */
	altAfterWechat: {
		marginTop: 14,
	},
	/**
	 * 文字链：不用 <a>，这几个是切页签不是跳转，按钮的键盘语义才对。
	 * 12.5px 的字自带 padding 才有够大的点击区域（纯文字链在触屏上只有十几 px 高）。
	 */
	link: {
		border: 0,
		background: 'none',
		padding: '8px 0',
		font: 'inherit',
		fontSize: 12.5,
		color: t.palette.celadonInk,
		cursor: 'pointer',
		textDecoration: 'underline',
	},
	linkMuted: {
		color: t.palette.ink3,
	},
	/** 游客说明：这套改版里「不登录也能写谱」是个明确卖点，得写清楚代价 */
	guestNote: {
		marginTop: 22,
		padding: '11px 13px',
		borderRadius: t.radius.sm,
		background: t.palette.paper2,
		border: `1px solid ${t.palette.line}`,
		fontSize: 12.5,
		color: t.palette.ink2,
		lineHeight: 1.65,
	},
	noteStrong: {
		color: t.palette.celadonInk,
		fontWeight: 500,
	},
	/** 提示条：错误朱砂、成功青瓷，同一套结构两种色 */
	tip: {
		borderRadius: t.radius.sm,
		padding: '8px 12px',
		fontSize: 13,
		marginBottom: 12,
		lineHeight: 1.6,
	},
	tipError: {
		background: t.palette.cinnabar + '1f',
		color: t.palette.cinnabar,
	},
	tipNotice: {
		background: t.palette.celadon + '1f',
		color: t.palette.celadonInk,
	},

	// ------------------------------------------------------------
	// 页签（微信 / 邮箱）
	// ------------------------------------------------------------
	/**
	 * 页签条。
	 *
	 * 位置在标题之上、卡片之内：标题「登录编曲练习库」说的是**这一页做什么**，
	 * 页签说的是**用哪种方式**，两者不是一层语义，所以页签在上、标题在下。
	 *
	 * 底边那根横线（tabRail）单独画，不给每个页签各画一条 ——
	 * 否则两条线在页签交界处会有一段重叠，视觉上像"这里有个接缝"。
	 */
	tabs: {
		display: 'flex',
		position: 'relative',
		marginBottom: 20,
		borderBottom: `1px solid ${t.palette.line}`,
	},
	tab: {
		flex: 1,
		position: 'relative',
		padding: '9px 0 11px',
		fontSize: 13.5,
		fontFamily: t.fonts.serif,
		color: t.palette.ink3,
		background: 'none',
		border: 0,
		cursor: 'pointer',
		transition: 'color .15s ease',
		'&:hover': {color: t.palette.ink2},
	},
	/**
	 * 选中态：字变深 + 底部一段朱砂短横。
	 *
	 * 用 `::after` 画横杠而不是给选中项加 border —— 加 border 会把选中项
	 * 顶高一像素（内容盒被挤），两个页签高度对不齐，来回切会看到轻微跳动。
	 * 绝对定位的 ::after 不参与布局，切换时行高完全不变。
	 */
	tabActive: {
		color: t.palette.cinnabar,
		fontWeight: 500,
		'&::after': {
			content: '""',
			position: 'absolute',
			left: '50%',
			transform: 'translateX(-50%)',
			bottom: -1,
			width: 26,
			height: 2,
			background: t.palette.cinnabar,
			borderRadius: 1,
		},
	},
	/**
	 * 页签里的微信图标槽。图标与文字之间留 6px —— 与站内其它按钮的图标
	 * 间距一致，两个页签的视觉重心才不会一左一右。
	 *
	 * 颜色交给 SVG 的 `fill="currentColor"` 继承页签文字色：未选中是墨灰、
	 * 选中是朱砂。**不要写死白色** —— 那是给旧的绿色按钮用的，搬到浅色
	 * 页签上图标会整个看不见。
	 */
	wechatIcon: {
		display: 'inline-block',
		verticalAlign: '-3px',
		marginRight: 6,
	},
	/**
	 * 二维码容器。
	 *
	 * 宽度 300px 是**微信官方 WxLogin 组件的 iframe 宽度**：qrconnect 在
	 * iframe 里渲染的紧凑面板（280px 二维码 + 提示文字）就是按 300×400
	 * 设计的，跟它对齐二维码自然居中。之前用 400px 宽（独立页尺寸），
	 * 面板挤在大框左上角，看着就歪。
	 *
	 * 用 `overflow:hidden` + 圆角把微信页的底色裁成圆角，否则那块直角底色
	 * 会硬生生贴在我们圆角卡片的纸面上，像贴了张便签。
	 *
	 * 底色跟着明暗走（见 `withColorScheme`）：iframe 加载完之前露出来的是这
	 * 一层，颜色不对就会闪一下。暗色用 `#121212` —— 微信暗色页的实测取色，
	 * 不是随手挑的深灰，差一点点在圆角边缘就能看出接缝。
	 */
	qrFrame: {
		width: 300,
		margin: '0 auto',
		borderRadius: t.radius.sm,
		overflow: 'hidden',
		border: `1px solid ${t.palette.line}`,
		background: t.resolved === 'dark' ? '#121212' : '#fff',
		lineHeight: 0,
	},
	/**
	 * 二维码下方的说明。
	 *
	 * 单独一段而不是塞进微信页里：我们控制不了 iframe 内的内容，
	 * 提示文案必须留在自己这边。
	 */
	qrHint: {
		marginTop: 14,
		textAlign: 'center',
		fontSize: 12.5,
		color: t.palette.ink3,
		lineHeight: 1.8,
	},
	qrHintStrong: {
		color: t.palette.celadonInk,
	},
	/** 二维码加载中/加载失败的占位：与二维码同尺寸，避免加载完页面高度跳动 */
	qrLoading: {
		height: 400,
		display: 'grid',
		placeItems: 'center',
		padding: '0 24px',
		textAlign: 'center',
		lineHeight: 1.7,
		color: t.palette.ink3,
		fontSize: 13,
		background: t.palette.paper2,
	},
	/**
	 * 未勾选协议时代替二维码的占位。
	 *
	 * 高度与二维码一样是 400 —— 勾选后 iframe 原地顶上，卡片高度不变，
	 * 勾选框不会在眼前往下跳一截。内容不画图标、不放按钮：这一格唯一的
	 * 动作在它下方的勾选框里，格子中间再放一个按钮就成了两条并行的路。
	 */
	qrGate: {
		height: 400,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 10,
		padding: '0 26px',
		textAlign: 'center',
		lineHeight: 1.8,
		color: t.palette.ink3,
		fontSize: 12.5,
		background: t.palette.paper2,
	},
	qrGateTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 14,
		color: t.palette.ink,
	},
	/** 第三行说明：比正文再淡一档 —— 它是补充，不是要读的话 */
	qrGateNote: {
		marginTop: 2,
		fontSize: 12,
		color: t.palette.ink3,
		opacity: 0.85,
	},
	/**
	 * 协议勾选行。
	 *
	 * 放在二维码**下面**而不是上面：视线顺序是「扫码 → 读提示 → 勾选」，
	 * 与人在手机上从上往下读的顺序一致；更实际的原因是勾选框在上面时，
	 * 勾完二维码在下方长出来，卡片会往上顶、标题跟着位移，看着像跳了一下。
	 *
	 * 输入框与文字分开成 `<input>` + `<label htmlFor>` + 独立按钮，而不是
	 * 常见的 `<label>` 套一切：`<label>` 里的按钮被点时会连带激活勾选框
	 * （点「查看协议」反而把自己勾上了），法律上这个"勾"必须是用户自己的意思。
	 */
	agreeRow: {
		marginTop: 14,
		display: 'flex',
		alignItems: 'flex-start',
		gap: 7,
		fontFamily: t.fonts.serif,
		fontSize: 13,
		lineHeight: 1.7,
		color: t.palette.ink2,
	},
	agreeBox: {
		flex: 'none',
		width: 15,
		height: 15,
		margin: '3px 0 0',
		accentColor: t.palette.cinnabar,
		cursor: 'pointer',
	},
	/** 只有「我已阅读并同意」这几个字可点（切换勾选）；协议名是另一颗按钮 */
	agreeLabel: {
		cursor: 'pointer',
	},
	/** 两份协议之间的联词。不给它可点区域，免得点空档以为能开全文 */
	agreeJoin: {
		padding: '0 2px',
	},
	/** 「和 + 第二份文档名」整体换行，避免「和」孤零零挂在行尾 */
	agreeTail: {
		display: 'inline-block',
		whiteSpace: 'nowrap' as const,
	},
	agreeLink: {
		border: 0,
		background: 'none',
		padding: 0,
		font: 'inherit',
		fontSize: 13,
		color: t.palette.celadonInk,
		cursor: 'pointer',
		textDecoration: 'underline',
	},
	/**
	 * 扫码结果面板（成功 / 失败）：与二维码同高，原地替换 iframe。
	 *
	 * 结果发生在 iframe 里，父窗口能控制的只有这个框 —— 所以提示必须
	 * 摆回框的原位置，用户视线上没有断点。
	 */
	qrResult: {
		height: 400,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 14,
		padding: '0 24px',
		textAlign: 'center',
		lineHeight: 1.7,
		color: t.palette.ink3,
		fontSize: 13,
		background: t.palette.paper2,
	},
	qrResultIcon: {
		fontSize: 34,
		lineHeight: 1,
		color: t.palette.celadonInk,
	},
	qrRetryBtn: {
		marginTop: 4,
		padding: '8px 22px',
		border: 'none',
		borderRadius: t.radius.sm,
		background: t.palette.celadonInk,
		color: '#fff',
		fontSize: 13,
		cursor: 'pointer',
	},
})

const useStyles = () => useThemedStyles(stylesFactory)

/**
 * 微信官方气泡图标（内联 SVG）。
 *
 * 为什么不引第三方图标包：整个应用只在这一个地方用到它，为了一个 18px 的
 * 图形把整个图标库打进登录页的 chunk 不划算。形状也简化过 —— 只保留
 * 「一个气泡 + 两个点」，微信那个双气泡在 18px 下两泡几乎糊在一起。
 *
 * 颜色用 `currentColor` 继承页签文字色（见 `wechatIcon` 样式里的注释）：
 * 写死白色在浅色页签上就是隐形图标。
 */
function WechatIcon() {
	return <svg className={useStyles().wechatIcon} width="18" height="18"
		viewBox="0 0 24 24" aria-hidden="true">
		<path fill="currentColor"
			d="M9.2 3C5.2 3 2 5.7 2 9.1c0 1.9 1.1 3.6 2.8 4.7l-.7 2.1 2.4-1.2c.8.2 1.7.4 2.7.4h.5a5.4 5.4 0 0 1-.2-1.5c0-3.1 3-5.6 6.7-5.6h.6C15.9 5.3 12.9 3 9.2 3zM6.8 8.1a.95.95 0 1 1 0-1.9.95.95 0 0 1 0 1.9zm4.9 0a.95.95 0 1 1 0-1.9.95.95 0 0 1 0 1.9z" />
		<path fill="currentColor"
			d="M22 13.6c0-2.7-2.7-4.9-6-4.9s-6 2.2-6 4.9 2.7 4.9 6 4.9c.7 0 1.4-.1 2-.3l1.9 1-.5-1.6c1.6-.9 2.6-2.4 2.6-4zm-8-1.5a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6zm4 0a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6z" />
	</svg>
}

/**
 * 从 hash 里取出微信回调带回来的错误文案。
 *
 * ## 为什么是 `#login?error=...` 而不是 `#/login?...`
 *
 * 前端 `parseHash()` 把 `#` 去掉后做**全等**比较（`hash === 'login'`），
 * 所以带斜杠的 `#/login` 会被判成未知页面弹回首页 —— 用户看到的就是
 * 「扫码后莫名跳回首页，什么提示都没有」。后端 `wechat_callback` 的跳转
 * 目标必须与这里保持一致，两边都**不能**加斜杠。
 *
 * query 部分用 `URLSearchParams` 解析，别手写 `split('&')` ——
 * 错误文案里有中文与空格，手写解析要额外处理 encodeURIComponent 的
 * 正反变换，写错就是"提示里出现 %E4%B8"。
 *
 * 取完立刻把 hash 抹掉：否则用户刷新页面会再看到一次同样的提示，
 * 而这时它已经是过期信息了。
 */
function takeWechatError(): string {
	const raw = location.hash.replace(/^#/, '')
	const q = raw.indexOf('?')
	if (q < 0) return ''
	const msg = new URLSearchParams(raw.slice(q + 1)).get('error') || ''
	if (!msg) return ''
	// 抹掉 query，留下干净的 #login —— 刷新时不再重复弹这条提示
	history.replaceState(null, '', location.pathname + '#login')
	return msg
}

/**
 * 给微信二维码地址带上颜色模式。
 *
 * ## 为什么可以这么做
 *
 * 微信 `qrconnect` 支持 `color_scheme=auto|dark|light`，但**只在 iframe 内
 * 生效** —— 它的脚本判断 `window.top != window` 才应用，顶层打开就忽略。
 * 我们恰好是 iframe（`self_redirect=true`），所以正好吃到这个能力：
 * 传 `dark` 后登录页换成深色底、浅色文字，二维码本身仍是白底黑码。
 *
 * 反色二维码（`filter: invert()`）确实能让整块变暗，但它把一个**必须被
 * 摄像头识别**的图形改了对比方向 —— 部分扫码器会读不出。微信自己的暗色
 * 模式也没那么做，而是只改周围留白。跟着官方走，不赌可扫性。
 *
 * ## 参数位置
 *
 * 必须落在 `#wechat_redirect` **之前**。那是 URL 的 fragment，
 * 追加到它后面就成了 fragment 的一部分，微信的 `[?&]name=` 扫不到。
 *
 * 也**不要**用 `new URL()` + `searchParams.set()` 来做这件事：那会把整个
 * query 解码再重新编码一遍，`redirect_uri` 里那些 `%3A%2F%2F` 就有了被
 * 改写的机会 —— 而这个地址是后端按 embed 场景精确拼出来的，改坏一个字节
 * 回调就对不上。纯字符串插入最稳：只加参数，不碰已有内容。
 */
function withColorScheme(url: string, scheme: 'light' | 'dark'): string {
	const param = `color_scheme=${scheme}`
	const at = url.indexOf('#')
	const head = at < 0 ? url : url.slice(0, at)
	const tail = at < 0 ? '' : url.slice(at)
	const last = head.slice(-1)
	const sep = head.includes('?') ? (last === '&' || last === '?' ? '' : '&') : '?'
	return `${head}${sep}${param}${tail}`
}

/**
 * 登录方式页签。
 *
 * 微信在前：它是**唯一能注册**的入口，新用户走到这里要能一眼看见。
 * 邮箱在后：给存量账号与已绑邮箱的人用，凭证是验证码而不是密码。
 */
type Tab = 'wechat' | 'email'

export function AuthPage(props: {onDone: () => void, onSkip?: () => void, onHome?: () => void}) {
	const classes = useStyles()
	const {ready} = useAuth()
	/** 当前明暗，用来给二维码挑颜色模式（见 withColorScheme） */
	const {resolved} = useTheme()
	/** 默认微信 —— 新用户只有这一条注册路径，不该让他先看到"我没有账号"的邮箱页 */
	const [tab, setTab] = useState<Tab>('wechat')

	// ---- 邮箱验证码 ----
	const [email, setEmail] = useState('')
	const [code, setCode] = useState('')
	const [sending, setSending] = useState(false)
	const [sent, setSent] = useState(false)
	const [cooldown, setCooldown] = useState(0)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState('')
	const [notice, setNotice] = useState('')

	// ---- 微信扫码 ----
	const [wechatBusy, setWechatBusy] = useState(false)
	/**
	 * 后端给的二维码**基础**地址（不含颜色模式）。为 null 时不渲染 iframe。
	 *
	 * 页签**恒显示、不做可用性探测**：之前是先打一次 `/auth/wechat/qr`
	 * 探测成功才渲染页签，那次往返要一两秒，用户看到的就是「页签迟到的
	 * 两秒后才冒出来」。可用性在真正取二维码时自然见分晓 —— 取不到就在
	 * 页签内显示原因，不比隐藏入口差，还省一次无谓的请求。
	 *
	 * 与 iframe 实际用的 `qrSrc` 分开存：明暗主题是可以在页面里随时切的，
	 * 而地址里只差一个 query。存基础地址 + 派生，切换主题就不必再往返一次
	 * `/auth/wechat/qr`（那会换一个 state，把正在扫的码作废）。
	 */
	const [qrBase, setQrBase] = useState<string | null>(null)
	/**
	 * iframe 实际 src = 基础地址 + 当前主题的颜色模式。
	 *
	 * 主题一变 iframe 会重新挂载（src 变了）—— 微信会重新出一张码，
	 * state 不变（同一个 URL），旧码随即失效。这是可接受的代价：
	 * 用户主动切主题时本来就不在扫码动作的中途。
	 */
	const qrSrc = useMemo(
		() => (qrBase ? withColorScheme(qrBase, resolved) : null),
		[qrBase, resolved],
	)
	/** 取二维码失败的原因（微信未配置等）。非空时在页签内显示，而不是顶部的 error 条 */
	const [qrError, setQrError] = useState('')
	/**
	 * 扫码结果状态：'idle' 等着扫 / 'success' 登录成功 / 'failed' 登录失败。
	 *
	 * 为什么要有它：扫码结果发生在 **iframe 里**（回调、换 token、种 cookie
	 * 都在那个 300×400 的框内完成），父窗口唯一能感知的方式是中转页的
	 * postMessage。收到消息后在**扫码框原位置**给出结果 —— 成功显示
	 * 「登录成功」再跳转；失败显示原因和「重新扫码」。什么也不显示的话，
	 * 用户看到的是二维码框突然白掉，不知道自己到底登上没有。
	 */
	const [qrStatus, setQrStatus] = useState<'idle' | 'success' | 'failed'>('idle')
	/**
	 * 是否已勾选**两份**协议（《用户服务协议》+《曲谱公开分享免责声明》）。
	 *
	 * 一颗勾选框覆盖两份文档：注册是"建号"这一个动作，两份文档分别管
	 * 账号本身与以后公开作品的责任，都在这一步一起表态。分开两颗会让人
	 * 以为可以只同意其中一份 —— 而只同意账号协议的人，公开作品时仍是没授权。
	 *
	 * 这里是二维码本身的前置条件：没勾就不取码、不显示码。取消勾选会把
	 * 已取到的码丢掉（`setQrBase(null)`）—— 留着一个能扫的码，等于勾选框只是装饰。
	 */
	const [agreed, setAgreed] = useState(false)
	/** 协议全文弹窗（只展示，不改勾选状态：读完关掉，勾不勾由用户自己决定） */
	const [docOpen, setDocOpen] = useState<'user' | 'share' | null>(null)
	/**
	 * 「完善账号」弹窗。微信直登的新账号没有任何第二凭证 ——
	 * 丢了微信号就等于丢了账号，所以登录成功后引导一次。
	 * 可以跳过（`skippable`）：登录页上逼人填手机号是最劝退的一步。
	 */
	const [completeOpen, setCompleteOpen] = useState(false)

	// 已登录则直接进入，不再重复引导补凭证（设置页里有常驻入口）
	useEffect(() => {
		void getSessionUser().then(u => {
			if (u) props.onDone()
		})
	}, [])

	/**
	 * 首帧从 hash 里取微信回调带回来的错误。
	 *
	 * 用 `useState` 的惰性初始化而不是 effect：effect 里 setState 会让
	 * 登录页先渲染一帧「无提示」，然后才冒出错误条 —— 读起来像闪烁。
	 * 惰性初始化在首次渲染前就把值算好了。
	 */
	const [wechatError] = useState(() => takeWechatError())

	/**
	 * 切到微信页签时才去取真正给 iframe 用的地址（embed 版，回调走 /callback/embed）。
	 *
	 * @param force `refreshQr` 必须传 true：它刚 `setQrBase(null)`，但本函数
	 * 闭包里读到的 `qrSrc` 还是**旧渲染的值**（React 状态更新是异步的），
	 * 不强制的话会在守卫处直接 return，「重新扫码」按了跟没按一样。
	 */
	const loadQr = async (force = false) => {
		// 没勾选协议就拿不到二维码 —— 这是本页唯一的"注册前必须同意"闸门，
		// 后端在回调建号时也会再挡一次（那边挡的是绕过前端的调用）。
		if (!agreed) return
		if (!force && (qrSrc || wechatBusy)) return
		setWechatBusy(true)
		setQrError('')
		setQrStatus('idle')
		try {
			// 两份文档的版本一起带去留痕：勾的是一颗框，同意的是两份文档，
			// 只记主协议版本会让「他同意的是哪一版公开声明」无从回答。
			const url = await fetchWechatQrUrl(true, USER_AGREEMENT.version, SHARE_DISCLAIMER.version)
			if (!url) {
				// 后端没配微信（AppID 没下来/环境变量丢了）。
				// 入口不消失 —— 在页签内说清原因，给一条回邮箱的出路。
				setQrError('微信登录当前不可用，请先用邮箱登录')
				return
			}
			setQrBase(url)
		} catch (err: any) {
			setQrError(translateAuthError(err.message))
		} finally {
			setWechatBusy(false)
		}
	}

	/** 切到微信页签、或刚勾上协议时取二维码；已有则复用（state 保留） */
	useEffect(() => {
		if (tab !== 'wechat' || !agreed) return
		void loadQr()
	}, [tab, agreed])

	/** iframe 首帧是否已加载完。用于把占位块换成 iframe，切换时高度不跳 */
	const [qrLoaded, setQrLoaded] = useState(false)

	/**
	 * 二维码过期后重来。
	 *
	 * 微信的码约 3 分钟过期，过期后它页面上的「点击刷新」是**微信自己的
	 * 控件、我们跨域点不到**。所以自己提供一个：清掉 src 再重新取一条，
	 * 新的 state 与新的二维码一起到。
	 */
	const refreshQr = async () => {
		setQrBase(null)
		setQrLoaded(false)
		setQrError('')
		setQrStatus('idle')
		await loadQr(true)
	}

	/** 验证码倒计时。后端也按 60 秒卡，这里只是不让用户白点一次 */
	useEffect(() => {
		if (cooldown <= 0) return
		const id = setTimeout(() => setCooldown(n => n - 1), 1000)
		return () => clearTimeout(id)
	}, [cooldown])

	/**
	 * 接收 iframe 里中转页发来的扫码结果通知。
	 *
	 * ## 为什么需要这个桥
	 *
	 * 扫码授权后，微信在 **iframe 内部** 导航到回调地址（`self_redirect=true`
	 * 时其脚本走 `window.location = t`，只导航 iframe 自己）。后端在 iframe
	 * 里完成换 token、种 cookie，然后回一个中转页，向 parent postMessage：
	 *
	 * - 成功 `{type:'wechat-done', uid}` → 框内显示「登录成功」，
	 *   问后端确认真身后跳走；
	 * - 失败 `{type:'wechat-error', message}` → 框内显示原因，
	 *   留一个「重新扫码」入口（refreshQr）。
	 *
	 * 父窗口自始至终没收到任何请求，内存里的会话缓存还是 null，
	 * 所以收到成功通知后必须**问一次后端**（refreshSessionAfterWechat）。
	 *
	 * ⚠️ 必须校验 `event.origin`：不校验的话，任何能往我们页面塞 iframe 的
	 * 站点都能发一条伪造的「登录成功」过来。只认同源。
	 */
	useEffect(() => {
		function onMessage(e: MessageEvent) {
			if (e.origin !== location.origin) return
			const d = e.data
			if (!d || typeof d !== 'object') return
			if (d.type === 'wechat-done') {
				// 先把结果摆进扫码框，让用户知道"成了"，再确认。
				setQrStatus('success')
				setQrBase(null)
				// 中转页只说"完成了"，到底是谁得问后端 —— 它自己也不知道是谁，
				// 且中间隔了一层 iframe，直接信它等于把鉴权交给前端。
				void refreshSessionAfterWechat().then(u => {
					if (!u) {
						// cookie 没种上（比如浏览器拦了第三方 cookie）—— 当成失败处理
						setQrStatus('failed')
						setQrError('登录状态确认失败，请重新扫码')
						return
					}
					// 微信直登的新账号还没绑邮箱/手机号：先让他补一种，
					// 否则一旦换号或丢号，这个账号就再也进不来了。
					// 上次勾过「不再提示」就不再自动弹 —— 设置页「登录方式」里
					// 的入口一直都在，不弹不等于绑不了。
					if (u.need_credential && !credentialPromptMuted()) {
						setCompleteOpen(true)
						return
					}
					props.onDone()
				})
				return
			}
			if (d.type === 'wechat-error') {
				setQrStatus('failed')
				setQrBase(null)
				setQrError(typeof d.message === 'string' && d.message
					? d.message : '登录失败，请重新扫码')
			}
		}
		window.addEventListener('message', onMessage)
		return () => window.removeEventListener('message', onMessage)
	}, [])

	/** 切页签时清掉提示：两个页签的报错各说各的事，留着只会误导 */
	function switchTab(next: Tab) {
		setTab(next)
		setError('')
		setNotice('')
	}

	/**
	 * 勾选 / 取消两份协议（服务协议 + 公开分享声明，一颗框一起表过）。
	 *
	 * 取消时连二维码和扫码结果一起清掉：留一张能扫的码，勾选框就成了装饰；
	 * 而扫码结果（成功/失败）是"勾着的时候发生的事"，留着会让人以为不勾
	 * 也能继续。重新勾上时 effect 会去取一张**新**码（旧码已作废）。
	 */
	function toggleAgreed(next: boolean) {
		setAgreed(next)
		if (next) return
		setQrBase(null)
		setQrLoaded(false)
		setQrStatus('idle')
		setQrError('')
	}

	async function sendCode() {
		setError('')
		setNotice('')
		setSending(true)
		try {
			const {ttl} = await requestCode('email', email)
			setSent(true)
			setCooldown(60)
			setNotice(`验证码已发送，${Math.floor(ttl / 60)} 分钟内有效。没收到的话看一下垃圾邮件。`)
		} catch (err: any) {
			// 「邮箱未注册」现在在**发码**这一步就回来了（没注册不发信），
			// 不等到核销 —— 否则用户会对着一个永远填不对的验证码框发呆。
			setError(translateAuthError(err?.message || '', err?.code))
		} finally {
			setSending(false)
		}
	}

	async function submitEmail() {
		setError('')
		setNotice('')
		setBusy(true)
		try {
			await verifyCode('email', email, code)
			props.onDone()
		} catch (err: any) {
			// 发码阶段已经拦过未注册的地址，这里是兜底：发码后到核销之间
			// 那个账号可能被解绑/删除，此时仍要给出「未注册」而不是"验证码错"。
			setError(translateAuthError(err?.message || '', err?.code))
		} finally {
			setBusy(false)
		}
	}

	return <PageHeader
		onHome={props.onHome}
		actions={props.onSkip
			? [{key: 'skip', label: '先不登录，直接用', variant: 'outline' as const, mobileRow: true, onClick: props.onSkip},
				// 新开标签页读教程，不打断登录流程（与编辑器/编曲练习库的教程动作同一写法）
				{key: 'tutorial', label: '教程', href: '#tutorial' as const, newTab: true}]
			: [{key: 'tutorial', label: '教程', href: '#tutorial' as const, newTab: true}]}>
		<div className={classes.authWrap}>
			<div className={classes.auth}>
				<div className={classes.card}>
					<div className={classes.seal}>{BRAND_SEAL}</div>
					{/* 页签恒显示、不探测 —— 见 qrSrc 声明处的注释。 */}
					<div className={classes.tabs} role="tablist" aria-label="登录方式">
						<button type="button" role="tab" aria-selected={tab === 'wechat'}
							className={`${classes.tab} ${tab === 'wechat' ? classes.tabActive : ''}`}
							onClick={() => switchTab('wechat')}>
							<WechatIcon />微信登录
						</button>
						<button type="button" role="tab" aria-selected={tab === 'email'}
							className={`${classes.tab} ${tab === 'email' ? classes.tabActive : ''}`}
							onClick={() => switchTab('email')}>
							邮箱登录
						</button>
					</div>

					<h3 className={classes.title}>登录编曲练习库</h3>
					<p className={classes.sub}>
						{tab === 'wechat' ? '扫码即注册，不用填任何资料' : '用邮箱验证码登录，不需要密码'}
					</p>
					{error && <div className={`${classes.tip} ${classes.tipError}`}>{error}</div>}
					{/* 微信回调带回来的失败原因（扫码取消 / state 过期 / 微信报错）。
					    优先级同 error：两者不同时出现，但真撞上时 error 更新鲜。 */}
					{wechatError && !error &&
						<div className={`${classes.tip} ${classes.tipError}`}>{wechatError}</div>}
					{notice && <div className={`${classes.tip} ${classes.tipNotice}`}>{notice}</div>}

					{tab === 'email' ? <>
						<div className={classes.field}>
							<label className={classes.label} htmlFor="auth-mail">邮箱</label>
							<input className={classes.input} id="auth-mail" type="email" value={email}
								autoComplete="email"
								onChange={e => setEmail(e.target.value)}
								placeholder="you@example.com" />
						</div>
						<div className={classes.field}>
							<label className={classes.label} htmlFor="auth-code">验证码</label>
							<div className={classes.codeRow}>
								<input className={`${classes.input} ${classes.codeInput}`} id="auth-code"
									value={code} placeholder="6 位数字" inputMode="numeric" maxLength={6}
									autoComplete="one-time-code"
									onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
									onKeyDown={e => e.key === 'Enter' && submitEmail()} />
								<button type="button" className={`${tbClass(classes, 'outline')} ${classes.sendBtn}`}
									disabled={sending || cooldown > 0 || !email.trim()}
									onClick={sendCode}>
									{cooldown > 0 ? `${cooldown} 秒后重发` : (sent ? '重新发送' : '获取验证码')}
								</button>
							</div>
						</div>
						<button className={`${tbClass(classes, 'primary')} ${classes.submit}`}
							onClick={submitEmail} disabled={busy || !ready || code.length < 6}>
							{busy ? '登录中…' : '登录'}
						</button>
					</> : <>
						{/* 二维码 iframe：整块就摆在微信页签里，不再开新窗口。
						    尺寸 300×400 是微信官方 WxLogin 组件的默认尺寸 —— qrconnect
						    在 iframe 里渲染的紧凑面板（280px 二维码居中 + 提示文字）就是
						    按这个尺寸设计的。之前用 400×501（独立页的尺寸），面板在
						    大框里偏上不居中。别凭"看起来差不多"调，跟官方组件对齐最稳。 */}
						<div className={classes.qrFrame}>
							{/* 没勾协议：这一格先不取码，只说明下一步做什么。
							    挡在这里而不是把勾选框塞进格子里 —— 格子里再放一颗
							    按钮就与下方那颗重复，两个"同意"入口比没有更糟。 */}
							{!agreed &&
								<div className={classes.qrGate}>
									<span className={classes.qrGateTitle}>先同意协议，再扫码</span>
									{/* 断行按 300px 宽卡着写：这三行是这一格里唯一的字，
									    让它在两行半处自动折行会把「议》」单独甩到末行。 */}
									<span>勾选下方「我已阅读并同意」<br />这里会出现微信二维码</span>
									<span className={classes.qrGateNote}>两份协议需一起同意<br />否则无法注册新账号</span>
								</div>}
							{/* 扫码结果面板：结果发生在 iframe 里（回调/换 token/种 cookie
							    都在框内完成），父窗口靠中转页的 postMessage 得知后，
							    把结果摆回扫码框原位置 —— 成功给确认，失败给原因和重扫入口，
							    绝不能让框里白着或塞进一整个网站。 */}
							{agreed && qrStatus === 'success' &&
								<div className={classes.qrResult}>
									<span className={classes.qrResultIcon} aria-hidden="true">✓</span>
									登录成功，正在进入…
								</div>}
							{agreed && qrStatus === 'failed' &&
								<div className={classes.qrResult}>
									<span>{qrError}</span>
									<button type="button" className={classes.qrRetryBtn}
										onClick={refreshQr} disabled={wechatBusy}>重新扫码</button>
								</div>}
							{agreed && qrStatus === 'idle' && !qrSrc && !qrError &&
								<div className={classes.qrLoading}>二维码加载中…</div>}
							{agreed && qrStatus === 'idle' && qrError &&
								<div className={classes.qrResult}>
									<span>{qrError}</span>
									<button type="button" className={classes.qrRetryBtn}
										onClick={refreshQr} disabled={wechatBusy}>重新加载</button>
								</div>}
							{agreed && qrStatus === 'idle' && qrSrc && <iframe
								src={qrSrc}
								title="微信扫码登录"
								width="300"
								height="400"
								frameBorder={0}
								scrolling="no"
								onLoad={() => setQrLoaded(true)}
								style={{display: qrLoaded ? 'block' : 'none'}} />}
						</div>
						{agreed && <div className={classes.qrHint}>
							用微信「扫一扫」扫码登录，<span className={classes.qrHintStrong}>无需手机号</span><br />
							二维码约 3 分钟失效
							<button type="button" className={classes.link}
								onClick={refreshQr} disabled={wechatBusy}
								style={{marginLeft: 4}}>换一个</button>
						</div>}
						{/* 勾选框与协议名分开两颗元素（见 agreeRow 样式里的注释）：
						    点「查看协议」不该顺手把自己勾上。 */}
						<div className={classes.agreeRow}>
							<input className={classes.agreeBox} id="auth-agree" type="checkbox"
								checked={agreed}
								onChange={e => toggleAgreed(e.target.checked)} />
							<span>
								<label className={classes.agreeLabel} htmlFor="auth-agree">我已阅读并同意</label>
								<button type="button" className={classes.agreeLink}
									onClick={() => setDocOpen('user')}>《用户服务协议》</button>
								{/* 「和」与第二份文档捆成一个整块：卡片只有 300 多 px 宽，
								    不捆的话「和」会单独留在第一行末尾，看着像句子被打断 */}
								<span className={classes.agreeTail}>
									<span className={classes.agreeJoin}>和</span>
									<button type="button" className={classes.agreeLink}
										onClick={() => setDocOpen('share')}>《曲谱公开分享免责声明》</button>
								</span>
							</span>
						</div>
					</>}

					<div className={`${classes.alt} ${classes.altAfterWechat}`}>
						{/* 没有注册入口，也没有"忘记密码" —— 全站无密码。
						    这里只留一句指路：邮箱登不进来的人应该去走微信。 */}
						{tab === 'email' &&
							<button className={`${classes.link} ${classes.linkMuted}`}
								onClick={() => switchTab('wechat')}>
								还没账号？微信扫码即可注册
							</button>}
						{tab === 'wechat' &&
							<button className={`${classes.link} ${classes.linkMuted}`}
								onClick={() => switchTab('email')}>
								已绑过邮箱？用邮箱验证码登录
							</button>}
					</div>

					<div className={classes.guestNote}>
						<b className={classes.noteStrong}>不登录也能写谱</b>——编辑器照常可用，点保存时内容只暂存在这台设备的浏览器里，
						清缓存或换浏览器就会丢。写好之后再登录，可以把草稿一键收进编曲练习库。
					</div>
				</div>
			</div>

			{/* 页脚挂在 `authWrap` 里、**不是** `auth` 里：`auth` 是 `placeItems:center`
			    的一格，页脚进去会被当成第二格一起居中排版，卡片就不在屏幕中央了。
			    外面那个无名 div 是块级垫层，`footInner` 的居中要靠它（见样式里的注释）。 */}
			<div><div className={classes.footInner}><SiteFooter /></div></div>
		</div>
		{/* 协议全文：只读展示。读完关掉，勾不勾仍由用户自己决定 ——
		    弹窗里再放一颗"同意并关闭"就成了第二个同意入口。 */}
		{docOpen === 'user' && <LegalDocModal doc={USER_AGREEMENT} onClose={() => setDocOpen(null)} />}
		{docOpen === 'share' && <LegalDocModal doc={SHARE_DISCLAIMER} onClose={() => setDocOpen(null)} />}
		{/* 完善账号：微信直登的账号还没邮箱/手机号，登录成功后引导补一种。
		    可以跳过 —— 这一步是"为你好"，不该变成必须跨过的门槛。 */}
		{completeOpen && <CredentialModal
			skippable
			onClose={() => {
				setCompleteOpen(false)
				// 无论绑没绑都放行：绑定失败不该把人堵在登录页
				props.onDone()
			}}
			onBound={() => props.onDone()} />}
	</PageHeader>
}

/**
 * 把后端英文错误翻成中文。
 *
 * 后端多数文案本来就是中文（`fail()` 里直接写好的），所以这里只兜
 * Django / 旧 supabase 时代遗留的英文串。翻不了就原样显示 ——
 * 吞掉或换成"操作失败"会让排查失去唯一线索。
 *
 * 优先看 `code`：后端中文文案以后可能会改，`code` 是稳定契约。
 */
export function translateAuthError(message: string, code?: string): string {
	// 发码阶段（未登录）与核销阶段都可能回这个码，两处文案统一
	if (code === 'target_not_registered') return '该邮箱还没有注册，请先用微信扫码注册'
	if (message.includes('Invalid login')) return '验证码或邮箱不正确'
	if (message.includes('already registered')) return '该邮箱已注册，请直接登录'
	if (message.includes('valid email')) return '请输入有效的邮箱地址'
	if (message.includes('rate limit')) return '操作过于频繁，请稍后再试'
	if (message.includes('email_signup_disabled')) return '现在只能用微信注册'
	if (message.includes('target_not_registered')) return '该邮箱还没有账号，请先用微信扫码注册'
	if (message.includes('code_expired')) return '验证码已过期，请重新获取'
	if (message.includes('too_frequent')) return '请 60 秒后再获取验证码'
	return message
}
